import useEmblaCarousel from "embla-carousel-react";
import Autoplay from "embla-carousel-autoplay";
import { useCallback, useEffect, useState } from "react";
import DoubleQuotes from "../../assets/storeis-from-families/double-quotes.svg";
import { useTranslation } from "react-i18next";
import { useLocal } from "hooks/useLocal";

const defaultCards = [
  {
    description:
      "I hope you understand that I had 100 dilemmas! Our therapist actually helped my daughter develop her communication skills to achieve confidence and joy.",
    name: "Abir",
    role: "Parent",
  },
  {
    description:
      "I always had to find the right thing for my son Jad. I tried so many therapists! My son loves his sessions, and I can already see the difference in!",
    name: "Fateema",
    role: "Parent",
  },
  {
    description:
      "The time I had as a new mommy you can't measure my words with a number - discovering numuw was relieving and made me a better parent and understand my child better.",
    name: "Mishka",
    role: "Parent",
  },
  {
    description:
      "I hope you understand that I had 100 dilemmas! Our therapist actually helped my daughter develop her communication skills to achieve confidence and joy.",
    name: "Sara",
    role: "Parent",
  },
  {
    description:
      "I hope you understand that I had 100 dilemmas! Our therapist actually helped my daughter develop her communication skills to achieve confidence and joy.",
    name: "Layla",
    role: "Parent",
  },
  {
    description:
      "I hope you understand that I had 100 dilemmas! Our therapist actually helped my daughter develop her communication skills to achieve confidence and joy.",
    name: "Nora",
    role: "Parent",
  },
];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false,
  );
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

export function StoriesFromFamilies() {
  const isMobile = useIsMobile();

  const { t } = useTranslation(["common"]);
  const { direction, isArabic } = useLocal();
  const dirAttr: string | undefined = Array.isArray(direction)
    ? direction[0]
    : direction;

  const isRTL = dirAttr === "rtl";

  const [emblaRef, emblaApi] = useEmblaCarousel(
    {
      loop: true,
      align: "start",
      slidesToScroll: 1,
      direction: isRTL ? "rtl" : "ltr",
    },
    [Autoplay({ delay: 3500, stopOnInteraction: false })],
  );

  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    setScrollSnaps(emblaApi.scrollSnapList());
    emblaApi.on("select", onSelect);
    onSelect();
    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi, onSelect]);

  // Re-initialize embla when direction changes
  useEffect(() => {
    if (!emblaApi) return;
    emblaApi.reInit({ direction: isRTL ? "rtl" : "ltr" });
  }, [isRTL, emblaApi]);

  const scrollTo = useCallback(
    (index: number) => emblaApi && emblaApi.scrollTo(index),
    [emblaApi],
  );

  const header = t("storiesFromFamilies.header");
  const title = t("storiesFromFamilies.title");
  const cards =
    (t("storiesFromFamilies.cards", { returnObjects: true }) as
      | { description: string; name: string; role: string }[]
      | undefined) ?? defaultCards;

  return (
    <section
      dir={dirAttr}
      className={`bg-[#F8F8F8] ${isArabic ? "font-ibmplex" : "font-inter"}`}
    >
      <article className="py-20 px-6 max-w-[1400px] mx-auto">
        <div className="flex flex-col items-center justify-center gap-2">
          <p
            className={`text-[16px] text-center font-bold text-[#4242CE] ${isArabic ? "font-ibmplex" : "font-funnel"}`}
          >
            {header}
          </p>
          <div
            className={`bg-[#4242CE] h-[2px] ${isArabic ? "max-w-[140px]" : "max-w-[76px]"} w-full mt-1`}
          ></div>
        </div>
        <p
          className={`text-center text-2xl md:text-[32px] font-bold text-gray leading-[39px] mt-[18px] ${isArabic ? "font-ibmplex" : "font-funnel"}`}
        >
          {title}
        </p>

        <section className="mt-10">
          {/* Embla viewport */}
          <div
            className="overflow-hidden"
            ref={emblaRef}
            dir={isRTL ? "rtl" : "ltr"}
          >
            <div
              className="flex"
              style={{
                marginLeft: isRTL ? "0" : isMobile ? "-12px" : "-40px",
                marginRight: isRTL ? (isMobile ? "-12px" : "-40px") : "0",
              }}
            >
              {cards.map((card, index) => (
                <div
                  key={index}
                  className="flex-none min-w-0"
                  style={{
                    width: isMobile ? "100%" : "calc(100% / 3)",
                    paddingLeft: isRTL ? "0" : isMobile ? "12px" : "40px",
                    paddingRight: isRTL ? (isMobile ? "12px" : "40px") : "0",
                  }}
                >
                  <StoriesFromFamiliesCard icon={DoubleQuotes} {...card} />
                </div>
              ))}
            </div>
          </div>

          {/* Dots */}
          <div className="flex justify-center items-center gap-2 mt-8">
            {scrollSnaps.length !== 1 &&
              scrollSnaps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => scrollTo(index)}
                  aria-label={`Go to slide ${index + 1}`}
                  className={`rounded-full transition-all duration-300 ${
                    index === selectedIndex
                      ? "bg-[#000] w-3 h-3"
                      : "bg-gray-300 w-2.5 h-2.5"
                  }`}
                />
              ))}
          </div>
        </section>
      </article>
    </section>
  );
}

const StoriesFromFamiliesCard = ({
  icon,
  description,
  name,
  role,
}: {
  icon: string;
  description: string;
  name: string;
  role: string;
}) => {
  return (
    <article
      className="p-6 flex flex-col gap-4 rounded-2xl bg-white md:max-h-[300px] h-full"
      style={{ filter: "drop-shadow(0px 4px 14px rgba(0, 0, 0, 0.01))" }}
    >
      <img src={icon} alt="Double Quotes" className="w-[55px] h-11" />
      <p className="text-sm text-[#4C4C4C]">{description}</p>
      <div>
        <p className="font-bold text-sm">{name}</p>
        <p className="text-sm text-gray-500">{role}</p>
      </div>
    </article>
  );
};
